import type { ComponentType } from 'react'
import { ChevronDown, ChevronRight, Search, X } from 'lucide-react'
import { useEffect, useMemo, useRef, useState } from 'react'
import { NavLink, useLocation } from 'react-router-dom'
import { cn, Input } from '@amglobal/ui-shared'
import {
  buildStaffNavTree,
  countNavLeaves,
  expandedGroupIdsForPath,
  filterNavTree,
  mergeExpandedGroupIds,
  splitAccountFromTree,
  workGroupIds,
  type StaffNavNode,
} from '@/app/navigation/staff-nav-tree'
import { isLeafPathActive } from '@/app/navigation/nav-node'
import { buildStaffNavContext } from '@/app/navigation/staff-nav-context'
import { applyWorkspaceDeskNavProfile } from '@/app/navigation/workspace-desk-nav-profile'
import { resolveWorkspaceDeskFromHost } from '@/config/workspace-desk'
import { useMeSessionQuery } from '@/features/auth/hooks/use-me-session-query'

/** Show menu search once the tree has enough leaves to skim. */
const SEARCH_LEAF_THRESHOLD = 12

const DEPTH_PAD: Record<number, string> = {
  0: 'pl-3',
  1: 'pl-6',
  2: 'pl-9',
}

function depthPad(depth: number): string {
  return DEPTH_PAD[depth] ?? 'pl-9'
}

function NavLeaf({
  label,
  path,
  icon: Icon,
  disabled,
  depth,
  activeWhenPrefix,
  activeExcludePrefixes,
}: {
  label: string
  path: string
  icon: ComponentType<{ className?: string }>
  disabled?: boolean | undefined
  depth: number
  activeWhenPrefix?: boolean | undefined
  activeExcludePrefixes?: string[] | undefined
}) {
  const { pathname } = useLocation()
  const active = isLeafPathActive(
    pathname,
    path,
    activeWhenPrefix,
    activeExcludePrefixes,
  )

  if (disabled) {
    return (
      <div
        className={cn(
          'flex cursor-not-allowed items-center gap-2 rounded-md py-1.5 pr-2 text-sm text-text-on-sidebar/40 opacity-60',
          depthPad(depth),
        )}
      >
        <Icon className="h-4 w-4 shrink-0" aria-hidden />
        <span className="truncate">{label}</span>
      </div>
    )
  }

  return (
    <NavLink
      to={path}
      end={!activeWhenPrefix}
      aria-current={active ? 'page' : undefined}
      className={cn(
        'relative flex min-h-11 items-center gap-2 rounded-md py-2 pr-2 text-sm font-medium transition-colors outline-none ring-0 focus:outline-none focus-visible:outline-none focus-visible:ring-0 md:min-h-0 md:py-1.5',
        depthPad(depth),
        active
          ? 'bg-white/10 text-white'
          : 'text-text-on-sidebar hover:bg-surface-sidebar-hover hover:text-white',
      )}
    >
      {active ? (
        <span
          className="absolute bottom-1 left-0 top-1 w-0.5 rounded-full bg-brand-primary"
          aria-hidden
        />
      ) : null}
      <Icon className="h-4 w-4 shrink-0 opacity-80" aria-hidden />
      <span className="truncate">{label}</span>
    </NavLink>
  )
}

function NavGroupRow({
  node,
  depth,
  expanded,
  onToggle,
  pathname,
  expandedIds,
  onToggleGroup,
}: {
  node: Extract<StaffNavNode, { kind: 'group' }>
  depth: number
  expanded: boolean
  onToggle: () => void
  pathname: string
  expandedIds: Set<string>
  onToggleGroup: (id: string) => void
}) {
  const Icon = node.icon
  const hasActiveChild = expandedGroupIdsForPath([node], pathname).has(node.id)

  return (
    <div>
      <button
        type="button"
        onClick={onToggle}
        className={cn(
          'flex w-full items-center gap-2 rounded-md py-1.5 pr-2 text-left text-sm font-semibold transition-colors outline-none ring-0 focus:outline-none focus-visible:outline-none focus-visible:ring-0',
          depthPad(depth),
          hasActiveChild
            ? 'text-white'
            : 'text-text-on-sidebar/75 hover:bg-surface-sidebar-hover hover:text-white',
        )}
        aria-expanded={expanded}
      >
        {expanded ? (
          <ChevronDown className="h-3.5 w-3.5 shrink-0 opacity-70" aria-hidden />
        ) : (
          <ChevronRight className="h-3.5 w-3.5 shrink-0 opacity-70" aria-hidden />
        )}
        <Icon className="h-3.5 w-3.5 shrink-0 opacity-80" aria-hidden />
        <span className="truncate">{node.label}</span>
      </button>
      {expanded ? (
        <div className="mt-0.5 space-y-0.5">
          {node.children.map((child) => (
            <NavTreeNode
              key={child.id}
              node={child}
              depth={depth + 1}
              pathname={pathname}
              expandedIds={expandedIds}
              onToggleGroup={onToggleGroup}
            />
          ))}
        </div>
      ) : null}
    </div>
  )
}

function NavTreeNode({
  node,
  depth,
  pathname,
  expandedIds,
  onToggleGroup,
}: {
  node: StaffNavNode
  depth: number
  pathname: string
  expandedIds: Set<string>
  onToggleGroup: (id: string) => void
}) {
  if (node.kind === 'leaf') {
    return (
      <NavLeaf
        label={node.label}
        path={node.path}
        icon={node.icon}
        disabled={node.disabled}
        activeWhenPrefix={node.activeWhenPrefix}
        activeExcludePrefixes={node.activeExcludePrefixes}
        depth={depth}
      />
    )
  }

  return (
    <NavGroupRow
      node={node}
      depth={depth}
      expanded={expandedIds.has(node.id)}
      onToggle={() => onToggleGroup(node.id)}
      pathname={pathname}
      expandedIds={expandedIds}
      onToggleGroup={onToggleGroup}
    />
  )
}

export function SidebarNav() {
  const { pathname } = useLocation()
  const [search, setSearch] = useState('')
  const session = useMeSessionQuery()
  const knownGroupIdsRef = useRef<Set<string>>(new Set())
  const seededRef = useRef(false)

  const baseTree = useMemo(() => {
    const desk = resolveWorkspaceDeskFromHost(window.location.hostname)
    const ctx = applyWorkspaceDeskNavProfile(buildStaffNavContext(session.data), desk)
    return buildStaffNavTree(ctx)
  }, [session.data])

  const { main: mainTree, account } = useMemo(
    () => splitAccountFromTree(baseTree),
    [baseTree],
  )

  const [expandedIds, setExpandedIds] = useState<Set<string>>(
    () => mergeExpandedGroupIds(baseTree, pathname, null, new Set()),
  )

  useEffect(() => {
    setSearch('')
  }, [pathname])

  useEffect(() => {
    setExpandedIds((prev) =>
      mergeExpandedGroupIds(
        baseTree,
        pathname,
        seededRef.current ? prev : null,
        knownGroupIdsRef.current,
      ),
    )
    knownGroupIdsRef.current = workGroupIds(baseTree)
    seededRef.current = true
  }, [baseTree, pathname])

  const leafCount = useMemo(() => countNavLeaves(baseTree), [baseTree])
  const showSearch = leafCount > SEARCH_LEAF_THRESHOLD

  const displayMain = useMemo(
    () => (showSearch ? filterNavTree(mainTree, search) : mainTree),
    [mainTree, search, showSearch],
  )

  const displayExpanded =
    search.trim().length > 0 && showSearch
      ? new Set(
          [...displayMain, ...(account ? [account] : [])]
            .filter((n): n is Extract<StaffNavNode, { kind: 'group' }> => n.kind === 'group')
            .map((n) => n.id),
        )
      : expandedIds

  function toggleGroup(id: string) {
    setExpandedIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  return (
    <div className="flex flex-1 flex-col overflow-hidden">
      {showSearch ? (
        <div className="border-b border-white/10 px-3 py-2.5">
          <div className="relative">
            <Search
              className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-text-on-sidebar/50"
              aria-hidden
            />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search menu…"
              className="h-8 rounded-md border-white/10 bg-surface-sidebar-hover pl-8 pr-8 text-sm text-white placeholder:text-text-on-sidebar/45 focus-visible:ring-brand-primary/40"
              aria-label="Search menu"
            />
            {search.trim().length > 0 ? (
              <button
                type="button"
                onClick={() => setSearch('')}
                className="absolute right-1.5 top-1/2 flex h-5 w-5 -translate-y-1/2 items-center justify-center rounded text-text-on-sidebar/70 hover:bg-white/10 hover:text-white"
                aria-label="Clear menu search"
              >
                <X className="h-3 w-3" />
              </button>
            ) : null}
          </div>
        </div>
      ) : null}

      <nav className="flex-1 space-y-0.5 overflow-y-auto p-2" aria-label="Main">
        {displayMain.map((node) => (
          <NavTreeNode
            key={node.id}
            node={node}
            depth={0}
            pathname={pathname}
            expandedIds={displayExpanded}
            onToggleGroup={toggleGroup}
          />
        ))}
        {displayMain.length === 0 ? (
          <div className="space-y-2 px-3 py-4 text-center">
            <p className="text-xs text-text-on-sidebar/60">
              {search.trim()
                ? `No menu matches "${search.trim()}".`
                : 'No menu items for your account.'}
            </p>
            {search.trim() ? (
              <button
                type="button"
                onClick={() => setSearch('')}
                className="text-xs font-semibold text-brand-primary hover:underline"
              >
                Clear search and show all
              </button>
            ) : null}
          </div>
        ) : null}
      </nav>

      {account ? (
        <div className="shrink-0 border-t border-white/10 p-2">
          <NavTreeNode
            key={account.id}
            node={account}
            depth={0}
            pathname={pathname}
            expandedIds={displayExpanded}
            onToggleGroup={toggleGroup}
          />
        </div>
      ) : null}
    </div>
  )
}
